import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "@/components/ui/sheet";
import { CalendarBlank, Plus, Trash, GridFour, Rows } from "@phosphor-icons/react";
import { toast } from "sonner";

const MONTHS_ES = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
];

// Colors per absence type
const TYPE_STYLES = {
    vacaciones: "bg-emerald-500",
    ausencia: "bg-amber-500",
    baja: "bg-rose-500",
    formacion: "bg-sky-500",
    personal: "bg-violet-500",
    pausa: "bg-slate-400",
};

const TYPE_LABELS = {
    vacaciones: "Vacaciones",
    ausencia: "Ausencia personal",
    baja: "Baja médica",
    formacion: "Formación",
    personal: "Personal",
    pausa: "Pausa",
};

function daysInMonth(year, month0) {
    return new Date(year, month0 + 1, 0).getDate();
}

function isoDate(year, month0, day) {
    const m = String(month0 + 1).padStart(2, "0");
    const d = String(day).padStart(2, "0");
    return `${year}-${m}-${d}`;
}

function weekdayNum(year, month0, day) {
    // 0=Sun..6=Sat  → return 0=Mon..6=Sun for Euro
    const raw = new Date(year, month0, day).getDay();
    return (raw + 6) % 7;
}

function fmtH(n) {
    const v = Number(n) || 0;
    if (v === 0) return "0";
    return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

function todayIso() {
    const d = new Date();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${m}-${day}`;
}

export default function AdminAbsencesMatrix() {
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [view, setView] = useState("annual"); // annual | monthly
    const [personFilter, setPersonFilter] = useState("all");
    const [month, setMonth] = useState(now.getMonth()); // 0..11 for monthly view
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [officeOpen, setOfficeOpen] = useState(false);

    const load = async () => {
        setBusy(true);
        try {
            const r = await http.get("/admin/absences/matrix", { params: { year } });
            setData(r.data);
        } finally {
            setBusy(false);
        }
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [year]);

    const officeClosedSet = useMemo(() => {
        const s = new Set();
        (data?.office_closed || []).forEach((r) => s.add(r.day));
        return s;
    }, [data]);

    const legend = (
        <div
            className="flex flex-wrap items-center gap-4 text-xs"
            data-testid="matrix-legend"
        >
            <LegendChip color="bg-emerald-500" label="Vacaciones (ING_008 / aprobadas)" />
            <LegendChip color="bg-amber-500" label="Ausencia" />
            <LegendChip color="bg-rose-500" label="Baja médica" />
            <LegendChip color="bg-sky-500" label="Formación" />
            <LegendChip color="bg-blue-600" label="Horas trabajadas" />
            <LegendChip color="bg-slate-200" label="Fin de semana" />
            <LegendChip color="bg-orange-400" label="Oficina cerrada" />
            <LegendChip color="bg-rose-100 border border-rose-300" label="Sin imputar (laborable)" />
            <span className="text-slate-500">
                El nº en cada celda = horas imputadas ese día (trabajo + ausencia)
            </span>
        </div>
    );

    return (
        <div data-testid="page-absences-matrix">
            <PageHeader
                eyebrow="Vacaciones"
                title={`Matriz de vacaciones ${year}`}
                description="Visualiza todas las vacaciones y ausencias aprobadas del equipo."
                actions={
                    <div className="flex gap-2">
                        <Sheet open={officeOpen} onOpenChange={setOfficeOpen}>
                            <SheetTrigger asChild>
                                <Button
                                    variant="outline"
                                    className="rounded-sm border-slate-300"
                                    data-testid="office-closed-btn"
                                >
                                    <CalendarBlank size={14} className="mr-2" />
                                    Oficina cerrada
                                </Button>
                            </SheetTrigger>
                            <OfficeClosedSheet
                                year={year}
                                initial={data?.office_closed || []}
                                onChanged={load}
                                onClose={() => setOfficeOpen(false)}
                            />
                        </Sheet>
                    </div>
                }
            />
            <div className="p-6 lg:p-10 space-y-5">
                {/* Controls */}
                <div className="border border-slate-200 bg-white p-4 flex flex-wrap items-end gap-4">
                    <div>
                        <Label className="text-xs">Año</Label>
                        <Input
                            type="number"
                            min="2000"
                            max="2100"
                            value={year}
                            onChange={(e) => setYear(Number(e.target.value) || year)}
                            className="mt-1 rounded-sm border-slate-300 font-mono w-28"
                            data-testid="matrix-year"
                        />
                    </div>
                    <div className="flex bg-slate-100 rounded-sm border border-slate-200 overflow-hidden">
                        <button
                            onClick={() => setView("annual")}
                            className={`px-3 py-2 text-xs font-medium flex items-center gap-1 ${
                                view === "annual"
                                    ? "bg-[#002BAF] text-white"
                                    : "text-slate-600 hover:bg-slate-200"
                            }`}
                            data-testid="matrix-view-annual"
                        >
                            <GridFour size={12} /> Anual
                        </button>
                        <button
                            onClick={() => setView("monthly")}
                            className={`px-3 py-2 text-xs font-medium flex items-center gap-1 ${
                                view === "monthly"
                                    ? "bg-[#002BAF] text-white"
                                    : "text-slate-600 hover:bg-slate-200"
                            }`}
                            data-testid="matrix-view-monthly"
                        >
                            <Rows size={12} /> Mensual
                        </button>
                    </div>
                    {view === "monthly" && (
                        <div>
                            <Label className="text-xs">Mes</Label>
                            <Select
                                value={String(month)}
                                onValueChange={(v) => setMonth(Number(v))}
                            >
                                <SelectTrigger
                                    className="mt-1 rounded-sm border-slate-300 w-40"
                                    data-testid="matrix-month"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {MONTHS_ES.map((m, i) => (
                                        <SelectItem key={i} value={String(i)}>
                                            {m}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    <div>
                        <Label className="text-xs">Persona</Label>
                        <Select value={personFilter} onValueChange={setPersonFilter}>
                            <SelectTrigger
                                className="mt-1 rounded-sm border-slate-300 w-56"
                                data-testid="matrix-person"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Toda la plantilla</SelectItem>
                                {(data?.users || []).map((u) => (
                                    <SelectItem key={u.user_id} value={u.user_id}>
                                        {u.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="ml-auto">{legend}</div>
                </div>

                {busy && (
                    <div
                        className="text-center py-10 text-slate-500 text-sm"
                        data-testid="matrix-loading"
                    >
                        Cargando…
                    </div>
                )}

                {!busy && data && view === "annual" && (
                    <AnnualMatrix
                        data={data}
                        year={year}
                        officeClosedSet={officeClosedSet}
                        personFilter={personFilter}
                    />
                )}

                {!busy && data && view === "monthly" && (
                    <MonthlyMatrix
                        data={data}
                        year={year}
                        month={month}
                        officeClosedSet={officeClosedSet}
                        personFilter={personFilter}
                    />
                )}
            </div>
        </div>
    );
}

function LegendChip({ color, label }) {
    return (
        <div className="flex items-center gap-1.5">
            <span className={`w-3 h-3 inline-block ${color}`} />
            <span className="text-slate-600">{label}</span>
        </div>
    );
}

function totalsFor(uid, data) {
    const t = data.totals_by_user?.[uid] || {};
    const vac = t.vacaciones || 0;
    return vac;
}

/* --------- ANNUAL VIEW: 4 rows of 3 months --------- */
function AnnualMatrix({ data, year, officeClosedSet, personFilter }) {
    const users = (data.users || []).filter(
        (u) => personFilter === "all" || u.user_id === personFilter,
    );
    const chunks = [];
    for (let i = 0; i < 12; i += 3) chunks.push([i, i + 1, i + 2]);

    return (
        <div className="space-y-5" data-testid="annual-matrix">
            {chunks.map((months) => (
                <div
                    key={months[0]}
                    className="border border-slate-200 bg-white overflow-x-auto"
                >
                    <table className="text-[10px] border-collapse w-full">
                        <thead>
                            <tr>
                                <th
                                    rowSpan={2}
                                    className="bg-[#002BAF] text-white text-left px-2 py-1 sticky left-0 z-10 min-w-[190px]"
                                >
                                    <div className="text-xs font-semibold">Trabajador</div>
                                    <div className="text-[9px] font-normal opacity-80">
                                        Nombre (total)
                                    </div>
                                </th>
                                {months.map((m0) => (
                                    <th
                                        key={m0}
                                        colSpan={daysInMonth(year, m0)}
                                        className="bg-[#002BAF] text-white text-center px-1 py-1 text-xs font-semibold uppercase tracking-wide"
                                    >
                                        {MONTHS_ES[m0]}
                                    </th>
                                ))}
                            </tr>
                            <tr>
                                {months.map((m0) => {
                                    const days = daysInMonth(year, m0);
                                    return Array.from({ length: days }, (_, i) => {
                                        const day = i + 1;
                                        const wd = weekdayNum(year, m0, day);
                                        const isWeekend = wd >= 5;
                                        return (
                                            <th
                                                key={`${m0}-${day}`}
                                                className={`px-0.5 py-1 text-center font-mono border-l border-slate-100 ${
                                                    isWeekend
                                                        ? "bg-slate-100 text-slate-400"
                                                        : "text-slate-600"
                                                }`}
                                                style={{ minWidth: 14 }}
                                            >
                                                {day}
                                            </th>
                                        );
                                    });
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((u, uidx) => (
                                <tr
                                    key={u.user_id}
                                    className={uidx % 2 === 0 ? "bg-white" : "bg-slate-50/60"}
                                >
                                    <td className="px-2 py-1 text-slate-800 border-r border-slate-200 sticky left-0 bg-inherit z-10 whitespace-nowrap">
                                        <span className="text-slate-500 font-mono mr-1">
                                            {uidx + 1}.
                                        </span>
                                        <span className="font-medium">{u.name}</span>{" "}
                                        <span className="text-slate-500 font-mono">
                                            ({totalsFor(u.user_id, data)})
                                        </span>
                                    </td>
                                    {months.map((m0) => {
                                        const days = daysInMonth(year, m0);
                                        return Array.from({ length: days }, (_, i) => {
                                            const day = i + 1;
                                            const wd = weekdayNum(year, m0, day);
                                            const isWeekend = wd >= 5;
                                            const iso = isoDate(year, m0, day);
                                            const closed = officeClosedSet.has(iso);
                                            const type = data.by_user?.[u.user_id]?.[iso];
                                            const workH =
                                                Number(
                                                    data.work_hours_by_user?.[u.user_id]?.[iso],
                                                ) || 0;
                                            const vacH =
                                                Number(
                                                    data.vacation_hours_by_user?.[u.user_id]?.[
                                                        iso
                                                    ],
                                                ) || 0;
                                            const cls = type
                                                ? TYPE_STYLES[type] || "bg-emerald-500"
                                                : vacH > 0
                                                  ? "bg-emerald-500"
                                                  : workH > 0
                                                    ? "bg-blue-600"
                                                    : closed
                                                      ? "bg-orange-400"
                                                      : isWeekend
                                                        ? "bg-slate-100"
                                                        : "bg-white";
                                            const title = type
                                                ? `${u.name} — ${TYPE_LABELS[type] || type} ${iso}`
                                                : vacH > 0
                                                  ? `${u.name} — Vacaciones (${fmtH(vacH)} h · ING_008) · ${iso}`
                                                  : workH > 0
                                                    ? `${u.name} — ${fmtH(workH)} h trabajadas · ${iso}`
                                                    : closed
                                                      ? `Oficina cerrada · ${iso}`
                                                      : iso;
                                            return (
                                                <td
                                                    key={`${m0}-${day}`}
                                                    title={title}
                                                    className={`border border-slate-100 ${cls}`}
                                                    style={{ height: 18, minWidth: 14 }}
                                                    data-testid={
                                                        type || workH > 0 || vacH > 0
                                                            ? `annual-cell-${u.user_id}-${iso}`
                                                            : undefined
                                                    }
                                                />
                                            );
                                        });
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}
        </div>
    );
}

/* --------- MONTHLY VIEW: single month, larger cells --------- */
function MonthlyMatrix({ data, year, month, officeClosedSet, personFilter }) {
    const users = (data.users || []).filter(
        (u) => personFilter === "all" || u.user_id === personFilter,
    );
    const days = daysInMonth(year, month);
    const dayArr = Array.from({ length: days }, (_, i) => i + 1);

    return (
        <div
            className="border border-slate-200 bg-white overflow-x-auto"
            data-testid="monthly-matrix"
        >
            <table className="text-xs border-collapse w-full">
                <thead>
                    <tr>
                        <th
                            className="bg-[#002BAF] text-white text-left px-3 py-2 sticky left-0 z-10 min-w-[220px]"
                        >
                            <div className="text-sm font-semibold">
                                {MONTHS_ES[month]} {year}
                            </div>
                            <div className="text-[10px] opacity-80">Trabajador · Horas mes</div>
                        </th>
                        {dayArr.map((d) => {
                            const wd = weekdayNum(year, month, d);
                            const isWeekend = wd >= 5;
                            const iso = isoDate(year, month, d);
                            const closed = officeClosedSet.has(iso);
                            return (
                                <th
                                    key={d}
                                    className={`px-1 py-1 text-center border-l border-slate-100 font-mono text-[11px] ${
                                        closed
                                            ? "bg-orange-400 text-white"
                                            : isWeekend
                                              ? "bg-slate-200 text-slate-500"
                                              : "bg-slate-50 text-slate-700"
                                    }`}
                                    style={{ minWidth: 26 }}
                                    title={iso}
                                >
                                    <div>{d}</div>
                                    <div className="text-[9px] uppercase opacity-70">
                                        {["L", "M", "X", "J", "V", "S", "D"][wd]}
                                    </div>
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {users.map((u, uidx) => {
                        const workMap = data.work_hours_by_user?.[u.user_id] || {};
                        const absMap = data.absence_hours_by_user?.[u.user_id] || {};
                        const vacMap = data.vacation_hours_by_user?.[u.user_id] || {};
                        // month totals of hours + missing workdays
                        let monthHours = 0;
                        let missingDays = 0;
                        const tIso = todayIso();
                        for (let d = 1; d <= days; d++) {
                            const iso = isoDate(year, month, d);
                            const wd = weekdayNum(year, month, d);
                            const tot =
                                (Number(workMap[iso]) || 0) +
                                (Number(absMap[iso]) || 0) +
                                (Number(vacMap[iso]) || 0);
                            monthHours += tot;
                            if (
                                wd < 5 &&
                                tot === 0 &&
                                !officeClosedSet.has(iso) &&
                                iso <= tIso
                            )
                                missingDays++;
                        }
                        return (
                            <tr
                                key={u.user_id}
                                className={uidx % 2 === 0 ? "bg-white" : "bg-slate-50/60"}
                            >
                                <td className="px-3 py-2 text-slate-800 border-r border-slate-200 sticky left-0 bg-inherit z-10 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                        <span className="text-slate-400 font-mono text-xs">
                                            {uidx + 1}.
                                        </span>
                                        <div>
                                            <div className="font-medium">{u.name}</div>
                                            <div className="text-[10px] text-slate-500 font-mono flex items-center gap-2">
                                                <span data-testid={`month-hours-${u.user_id}`}>
                                                    {fmtH(monthHours)} h
                                                </span>
                                                {missingDays > 0 && (
                                                    <span
                                                        className="text-rose-600 font-semibold"
                                                        data-testid={`month-missing-${u.user_id}`}
                                                    >
                                                        · {missingDays} sin imputar
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                {dayArr.map((d) => {
                                    const wd = weekdayNum(year, month, d);
                                    const isWeekend = wd >= 5;
                                    const iso = isoDate(year, month, d);
                                    const closed = officeClosedSet.has(iso);
                                    const type = data.by_user?.[u.user_id]?.[iso];
                                    const workH = Number(workMap[iso]) || 0;
                                    const absH = Number(absMap[iso]) || 0;
                                    const vacH = Number(vacMap[iso]) || 0;
                                    const total = workH + absH + vacH;
                                    const isPastWorkday =
                                        !isWeekend && !closed && iso <= todayIso();
                                    const missing = isPastWorkday && total === 0 && !type;

                                    let cls = "bg-white";
                                    let textCls = "text-slate-700";
                                    if (type) {
                                        cls = TYPE_STYLES[type] || "bg-emerald-500";
                                        textCls = "text-white font-semibold";
                                    } else if (vacH > 0) {
                                        cls = "bg-emerald-500";
                                        textCls = "text-white font-semibold";
                                    } else if (closed) {
                                        cls = "bg-orange-400";
                                        textCls = "text-white";
                                    } else if (missing) {
                                        cls = "bg-rose-100";
                                        textCls = "text-rose-400";
                                    } else if (isWeekend) {
                                        cls = "bg-slate-100";
                                        textCls = "text-slate-400";
                                    } else if (total > 0) {
                                        cls = "bg-blue-50";
                                        textCls = "text-slate-800 font-medium";
                                    }

                                    const parts = [];
                                    if (workH > 0) parts.push(`Trabajo: ${fmtH(workH)}h`);
                                    if (vacH > 0)
                                        parts.push(`Vacaciones (ING_008): ${fmtH(vacH)}h`);
                                    if (type)
                                        parts.push(
                                            `${TYPE_LABELS[type] || type}: ${fmtH(absH)}h`,
                                        );
                                    if (missing) parts.push("Sin imputar");
                                    const title = `${u.name} · ${iso}${
                                        parts.length ? " — " + parts.join(" · ") : ""
                                    }`;

                                    return (
                                        <td
                                            key={d}
                                            title={title}
                                            className={`border border-slate-100 text-center font-mono text-[10px] ${cls} ${textCls}`}
                                            style={{ height: 30, minWidth: 34 }}
                                            data-testid={
                                                type || total > 0 || missing
                                                    ? `monthly-cell-${u.user_id}-${iso}`
                                                    : undefined
                                            }
                                        >
                                            {total > 0 ? fmtH(total) : missing ? "·" : ""}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

/* --------- OFFICE CLOSED DAYS SHEET --------- */
function OfficeClosedSheet({ year, initial, onChanged }) {
    const [items, setItems] = useState(initial);
    const [day, setDay] = useState(`${year}-08-15`);
    const [label, setLabel] = useState("");

    useEffect(() => {
        setItems(initial);
    }, [initial]);

    const add = async (e) => {
        e.preventDefault();
        try {
            await http.post("/office-closed", { day, label });
            toast.success("Día añadido");
            setLabel("");
            const r = await http.get("/office-closed", { params: { year } });
            setItems(r.data);
            onChanged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    const del = async (d) => {
        try {
            await http.delete(`/office-closed/${d}`);
            const r = await http.get("/office-closed", { params: { year } });
            setItems(r.data);
            onChanged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    return (
        <SheetContent className="rounded-none sm:max-w-md" data-testid="office-closed-sheet">
            <SheetHeader>
                <SheetTitle className="font-display">Días de oficina cerrada</SheetTitle>
                <SheetDescription className="text-xs text-slate-500">
                    Se marcarán en naranja en la matriz. Útil para cierres colectivos, festivos
                    empresariales, etc.
                </SheetDescription>
            </SheetHeader>
            <form
                onSubmit={add}
                className="mt-4 grid grid-cols-[auto_1fr_auto] gap-2 items-end"
            >
                <div>
                    <Label className="text-xs">Fecha</Label>
                    <Input
                        type="date"
                        value={day}
                        onChange={(e) => setDay(e.target.value)}
                        className="mt-1 rounded-sm border-slate-300 font-mono"
                        data-testid="office-day-input"
                        required
                    />
                </div>
                <div>
                    <Label className="text-xs">Etiqueta (opcional)</Label>
                    <Input
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        placeholder="Ej. Puente de agosto"
                        className="mt-1 rounded-sm border-slate-300"
                        data-testid="office-label-input"
                    />
                </div>
                <Button
                    type="submit"
                    className="rounded-sm bg-[#002BAF] hover:bg-[#001F82]"
                    data-testid="office-add"
                >
                    <Plus size={14} />
                </Button>
            </form>
            <div className="mt-5 border border-slate-200">
                {items.length === 0 ? (
                    <div className="p-4 text-sm text-slate-500 text-center">
                        Sin días configurados en {year}.
                    </div>
                ) : (
                    items.map((it) => (
                        <div
                            key={it.day}
                            className="flex items-center justify-between px-3 py-2 border-b border-slate-100 last:border-b-0"
                        >
                            <div>
                                <div className="text-sm font-mono">{it.day}</div>
                                {it.label && (
                                    <div className="text-xs text-slate-500">{it.label}</div>
                                )}
                            </div>
                            <button
                                onClick={() => del(it.day)}
                                className="text-slate-400 hover:text-red-600 p-1"
                                data-testid={`office-del-${it.day}`}
                            >
                                <Trash size={14} />
                            </button>
                        </div>
                    ))
                )}
            </div>
        </SheetContent>
    );
}
